import { FC, useEffect, useState, useCallback, useRef } from "react";
import { AutoModel } from "./AutoModel";
import { LockOutlined, MobileOutlined } from "@ant-design/icons";
import {
  updateProfileApi,
  updatePasswordApi,
  updatePhoneApi,
  logoutApi,
} from "../../service/user";
import { sendSmsCodeApi } from "../../service/login";
import { resolveAuthErrorMessage } from "../../utils/turnstile";
import { getMenuPermissionsApi } from "../../service/menu";
import { globalStore } from "../../utils/globalStore";
import { globalAlert } from "../../utils/globalAlert";
import { InitService } from "../../utils/initService";
import { getDicebearAvataaarsUrl } from "../../pages/home/dicebearAvatar";
import "./ProfileModal.scss";

export interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  userInfo: unknown;
  roleLabel: string;
}

interface ProfileForm {
  nickname: string;
  email: string;
  phone: string;
  account: string;
}

function readProfileFromUserInfo(userInfo: unknown): ProfileForm {
  const u = (userInfo || {}) as {
    nickname?: string;
    nickName?: string;
    nick_name?: string;
    realName?: string;
    name?: string;
    username?: string;
    email?: string;
    mobile?: string;
    phone?: string;
    account?: string;
  };
  return {
    nickname:
      u.nickname?.trim() ||
      u.nickName?.trim() ||
      u.nick_name?.trim() ||
      u.realName?.trim() ||
      u.name?.trim() ||
      u.username?.trim() ||
      "",
    email: u.email?.trim() || "",
    phone: u.mobile?.trim() || u.phone?.trim() || "",
    account: u.account?.trim() || u.username?.trim() || "",
  };
}

/** 短信场景：2 = 修改手机号（MEMBER_UPDATE_MOBILE） */
const SCENE_UPDATE_PHONE = 2;
/** 发送短信 60 秒倒计时 */
const SMS_COUNTDOWN_SECONDS = 60;
/** 新密码最小长度，与注册/忘记密码保持一致 */
const PASSWORD_MIN_LENGTH = 6;
/** 拉取防抖：相邻两次打开间隔小于该值则跳过拉取，防止 open 抖动循环请求 */
const FETCH_DEBOUNCE_MS = 1000;

/** 修改密码成功后执行登出：与 HomeShell.handleLogout 行为对齐 */
async function performLogout(): Promise<void> {
  try {
    await logoutApi();
  } catch {
    /* best-effort：忽略登出接口失败 */
  }
  try {
    localStorage.removeItem("token");
  } catch {
    /* noop */
  }
  InitService.clearUserData();
  window.location.replace("/login");
}

export const ProfileModal: FC<ProfileModalProps> = ({
  open,
  onClose,
  userInfo,
  roleLabel,
}) => {
  const [form, setForm] = useState<ProfileForm>(() =>
    readProfileFromUserInfo(userInfo)
  );
  const [phonePanelOpen, setPhonePanelOpen] = useState(false);
  const [passwordPanelOpen, setPasswordPanelOpen] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [code, setCode] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const smsTimerRef = useRef<number | null>(null);
  /** 上一次成功拉取接口的时间戳（ms），用于防抖 open 抖动触发的循环请求 */
  const lastFetchAtRef = useRef<number>(0);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submittingPhone, setSubmittingPhone] = useState(false);
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [submittingBasic, setSubmittingBasic] = useState(false);
  /** 打开时拉取到的昵称，作为「保存修改」是否触发 API 的对照基准 */
  const [originalNickname, setOriginalNickname] = useState<string>("");
  const [feedback, setFeedback] = useState<{ msg: string; type: "info" | "error" | "success" }>({
    msg: "",
    type: "info",
  });

  // 重置所有状态
  const resetAllState = useCallback(() => {
    const initial = readProfileFromUserInfo(userInfo);
    setForm(initial);
    // 以 props 中的昵称作为兜底基准，API 拉取成功后会被覆盖为最新值
    setOriginalNickname(initial.nickname || "");
    setPhonePanelOpen(false);
    setPasswordPanelOpen(false);
    setNewPhone("");
    setCode("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setFeedback({ msg: "", type: "info" });
    setSmsCountdown(0);
    if (smsTimerRef.current) {
      window.clearInterval(smsTimerRef.current);
      smsTimerRef.current = null;
    }
  }, [userInfo]);

  // 卸载时清理定时器
  useEffect(() => {
    return () => {
      if (smsTimerRef.current) {
        window.clearInterval(smsTimerRef.current);
        smsTimerRef.current = null;
      }
    };
  }, []);

  // 打开时：先 reset，再异步拉取最新 profile
  // 注意：仅依赖 [open]，不写回 globalStore，避免与 HomeShell 的订阅形成 setUserInfo 死循环。
  // 加 FETCH_DEBOUNCE_MS 防抖，防止 open 抖动时循环拉取。
  useEffect(() => {
    if (!open) return;
    const now = Date.now();
    if (now - lastFetchAtRef.current < FETCH_DEBOUNCE_MS) return;
    lastFetchAtRef.current = now;

    resetAllState();

    let cancelled = false;
    (async () => {
      try {
        const res = await getMenuPermissionsApi();
        if (cancelled) return;
        if (res && res.code === 0 && res.data?.user) {
          const d = res.data.user;
          const nickname = (d.nickname || "").trim();
          const email = (d.email || "").trim();
          const phone = (d.mobile || "").trim();
          const account = (d.username || "").trim();
          // 只更新本组件内的表单状态；不写回 globalStore，
          // 防止 HomeShell 订阅触发新 userInfo prop 进而重跑此 effect 形成循环。
          setForm({ nickname, email, phone, account });
          // 以 API 返回的昵称为基准，用于「保存修改」判定是否未改动
          setOriginalNickname(nickname);
        }
      } catch (err) {
        // 静默失败，使用 props 传入的 userInfo
        console.warn("ProfileModal: 获取最新用户信息失败", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 短信倒计时
  const startSmsCountdown = useCallback(() => {
    setSmsCountdown(SMS_COUNTDOWN_SECONDS);
    if (smsTimerRef.current) {
      window.clearInterval(smsTimerRef.current);
    }
    smsTimerRef.current = window.setInterval(() => {
      setSmsCountdown((prev) => {
        if (prev <= 1) {
          if (smsTimerRef.current) {
            window.clearInterval(smsTimerRef.current);
            smsTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /** 发送短信验证码 */
  const handleSendSms = useCallback(async () => {
    const mobile = newPhone.trim();
    if (!/^1\d{10}$/.test(mobile)) {
      setFeedback({ msg: "请输入正确的 11 位手机号码。", type: "error" });
      globalAlert.showError("请输入正确的 11 位手机号码");
      return;
    }
    if (smsCountdown > 0 || sendingSms) return;

    setSendingSms(true);
    try {
      const res = await sendSmsCodeApi({ mobile, scene: SCENE_UPDATE_PHONE });
      if (res && res.code === 0) {
        setFeedback({ msg: "验证码已发送，请注意查收短信。", type: "success" });
        globalAlert.showSuccess("验证码已发送");
        startSmsCountdown();
      } else {
        const msg = res?.msg || "验证码发送失败，请稍后重试";
        setFeedback({ msg, type: "error" });
        globalAlert.showError(msg);
      }
    } catch (err: any) {
      const msg = resolveAuthErrorMessage(err, "验证码发送失败，请稍后重试");
      setFeedback({ msg, type: "error" });
      globalAlert.showError(msg);
    } finally {
      setSendingSms(false);
    }
  }, [newPhone, smsCountdown, sendingSms, startSmsCountdown]);

  /** 提交：更换手机号 */
  const handleSubmitPhone = useCallback(async () => {
    const mobile = newPhone.trim();
    const smsCode = code.trim();
    if (!/^1\d{10}$/.test(mobile)) {
      setFeedback({ msg: "请输入正确的 11 位手机号码。", type: "error" });
      globalAlert.showError("请输入正确的 11 位手机号码");
      return;
    }
    if (!/^\d{4,6}$/.test(smsCode)) {
      setFeedback({ msg: "请填写短信验证码。", type: "error" });
      globalAlert.showError("请填写短信验证码");
      return;
    }
    setSubmittingPhone(true);
    try {
      const res = await updatePhoneApi({ mobile, code: smsCode });
      if (res && res.code === 0) {
        // 更新本地表单 + globalStore
        const masked = mobile.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
        setForm((prev) => ({ ...prev, phone: masked }));
        const current = (globalStore.getUserInfo?.() as any) || {};
        globalStore.setUserInfo({
          ...current,
          mobile,
          phone: mobile,
        });
        setFeedback({ msg: "手机号已更新。", type: "success" });
        globalAlert.showSuccess("手机号已更新");
        setPhonePanelOpen(false);
        setNewPhone("");
        setCode("");
      } else {
        const msg = res?.msg || "手机号更新失败，请稍后重试";
        setFeedback({ msg, type: "error" });
        globalAlert.showError(msg);
      }
    } catch (err: any) {
      const msg =
        err?.response?.msg ||
        err?.msg ||
        err?.detail ||
        err?.message ||
        "手机号更新失败，请稍后重试";
      setFeedback({ msg, type: "error" });
      globalAlert.showError(msg);
    } finally {
      setSubmittingPhone(false);
    }
  }, [newPhone, code]);

  /** 提交：修改登录密码 */
  const handleSubmitPassword = useCallback(async () => {
    if (!currentPassword) {
      setFeedback({ msg: "请输入当前密码。", type: "error" });
      globalAlert.showError("请输入当前密码");
      return;
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setFeedback({
        msg: `新密码至少需要 ${PASSWORD_MIN_LENGTH} 位字符。`,
        type: "error",
      });
      globalAlert.showError(`新密码至少需要 ${PASSWORD_MIN_LENGTH} 位字符`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFeedback({ msg: "两次输入的新密码不一致。", type: "error" });
      globalAlert.showError("两次输入的新密码不一致");
      return;
    }
    setSubmittingPassword(true);
    try {
      const res = await updatePasswordApi({
        oldPassword: currentPassword,
        newPassword,
      });
      if (res && res.code === 0) {
        setFeedback({ msg: "密码已更新，即将退出登录...", type: "success" });
        globalAlert.showSuccess("密码已更新，即将退出登录");
        setPasswordPanelOpen(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        // 关闭弹窗，避免日志清理后状态错位
        onClose();
        // 延迟一点让用户看到成功提示，再触发登出
        window.setTimeout(() => {
          void performLogout();
        }, 600);
      } else {
        const msg = res?.msg || "密码更新失败，请稍后重试";
        setFeedback({ msg, type: "error" });
        globalAlert.showError(msg);
      }
    } catch (err: any) {
      const msg =
        err?.response?.msg ||
        err?.msg ||
        err?.detail ||
        err?.message ||
        "密码更新失败，请稍后重试";
      setFeedback({ msg, type: "error" });
      globalAlert.showError(msg);
    } finally {
      setSubmittingPassword(false);
    }
  }, [currentPassword, newPassword, confirmPassword, onClose]);

  /** 底部「保存修改」：只保存基本资料（昵称），不传 email/mobile 以免覆盖 */
  const handleSaveBasic = useCallback(async () => {
    const nickname = form.nickname.trim();
    if (!nickname) {
      setFeedback({ msg: "昵称不能为空。", type: "error" });
      globalAlert.showError("昵称不能为空");
      return;
    }
    // 与打开时拉取到的昵称对比，未改动则不触发接口
    if (nickname === originalNickname.trim()) {
      const tip = "未进行昵称修改";
      setFeedback({ msg: tip, type: "info" });
      globalAlert.showInfo?.(tip);
      return;
    }
    setSubmittingBasic(true);
    try {
      const res = await updateProfileApi({ nickname });
      if (res && res.code === 0) {
        setForm((prev) => ({ ...prev, nickname }));
        const current = (globalStore.getUserInfo?.() as any) || {};
        globalStore.setUserInfo({ ...current, nickname });
        setOriginalNickname(nickname);
        setFeedback({ msg: "资料已更新。", type: "success" });
        globalAlert.showSuccess("资料已更新");
        window.setTimeout(() => {
          onClose();
        }, 450);
      } else {
        const msg = res?.msg || "资料更新失败，请稍后重试";
        setFeedback({ msg, type: "error" });
        globalAlert.showError(msg);
      }
    } catch (err: any) {
      const msg =
        err?.response?.msg ||
        err?.msg ||
        err?.detail ||
        err?.message ||
        "资料更新失败，请稍后重试";
      setFeedback({ msg, type: "error" });
      globalAlert.showError(msg);
    } finally {
      setSubmittingBasic(false);
    }
  }, [form.nickname, originalNickname, onClose]);

  const avatarSrc = getDicebearAvataaarsUrl(userInfo);
  const smsBtnDisabled = sendingSms || smsCountdown > 0;
  const smsBtnLabel = sendingSms
    ? "发送中"
    : smsCountdown > 0
    ? `${smsCountdown}s 后重发`
    : "发送验证码";

  return (
    <AutoModel
      open={open}
      onClose={onClose}
      title={
        <div>
          <h2>账户设置</h2>
          <p>个人资料与安全设置</p>
        </div>
      }
      size="md"
      contentMaxWidth={600}
      contentClassName="profile-modal-shell"
      bodyClassName="profile-modal-body"
      footerRight={
        <div className="profile-modal-actions">
          <button type="button" className="profile-btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="profile-btn profile-btn--primary"
            onClick={handleSaveBasic}
            disabled={submittingBasic}
          >
            {submittingBasic ? "保存中..." : "保存修改"}
          </button>
        </div>
      }
    >
      <div className="profile-modal-summary">
        <span className="profile-modal-avatar">
          <img
            className="profile-modal-avatar-img"
            src={avatarSrc}
            alt={form.nickname ? `${form.nickname} 的头像` : "用户头像"}
            width={44}
            height={44}
            decoding="async"
            loading="lazy"
          />
        </span>
        <div className="profile-modal-identity">
          <strong>{form.nickname || "未设置昵称"}</strong>
          <small>{form.account || "—"}</small>
        </div>
        <span className="profile-modal-role">{roleLabel}</span>
      </div>

      <section className="profile-modal-section">
        <div className="profile-modal-section-head">
          <h3>基本资料</h3>
          <p>昵称将显示在工作台和协作场景中</p>
        </div>
        <div className="profile-modal-grid">
          <label>
            <span>昵称</span>
            <input
              type="text"
              maxLength={24}
              value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
            />
          </label>
          <label>
            <span>邮箱地址</span>
            <input type="email" value={form.email} disabled />
          </label>
        </div>
      </section>

      <section className="profile-modal-section">
        <div className="profile-modal-section-head">
          <h3>账户安全</h3>
          <p>管理验证手机和登录密码</p>
        </div>
        <div className="profile-modal-security-list">
          <div className="profile-modal-security-item">
            <span className="profile-modal-security-icon">
              <MobileOutlined />
            </span>
            <div>
              <strong>手机号码</strong>
              <small>{form.phone || "未绑定"}</small>
            </div>
            <button
              type="button"
              onClick={() => setPhonePanelOpen((v) => !v)}
              aria-expanded={phonePanelOpen}
            >
              {phonePanelOpen ? "收起" : "更换"}
            </button>
            <button
              type="button"
              onClick={handleSubmitPhone}
              disabled={submittingPhone}
            >
              {submittingPhone ? "提交中..." : "提交"}
            </button>
          </div>
          {phonePanelOpen ? (
            <div className="profile-modal-security-panel">
              <label>
                <span>新手机号码</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  maxLength={11}
                  placeholder="请输入 11 位手机号码"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                />
              </label>
              <label>
                <span>短信验证码</span>
                <div className="profile-modal-code-input">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="请输入验证码"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <button
                    type="button"
                    className="profile-modal-sms-btn"
                    onClick={handleSendSms}
                    disabled={smsBtnDisabled}
                  >
                    {smsBtnLabel}
                  </button>
                </div>
              </label>
              <div data-turnstile-slot="" className="profile-modal-turnstile" aria-label="人机验证" />
            </div>
          ) : null}

          <div className="profile-modal-security-item">
            <span className="profile-modal-security-icon">
              <LockOutlined />
            </span>
            <div>
              <strong>登录密码</strong>
              <small>建议定期更换密码，保障账户安全</small>
            </div>
            <button
              type="button"
              onClick={() => setPasswordPanelOpen((v) => !v)}
              aria-expanded={passwordPanelOpen}
            >
              {passwordPanelOpen ? "收起" : "修改"}
            </button>
            <button
              type="button"
              onClick={handleSubmitPassword}
              disabled={submittingPassword}
            >
              {submittingPassword ? "提交中..." : "提交"}
            </button>
          </div>
          {passwordPanelOpen ? (
            <div className="profile-modal-security-panel profile-modal-password-panel">
              <label>
                <span>当前密码</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  placeholder="请输入当前密码"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </label>
              <label>
                <span>新密码</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder={`至少 ${PASSWORD_MIN_LENGTH} 位字符`}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </label>
              <label>
                <span>确认新密码</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="再次输入新密码"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </label>
            </div>
          ) : null}
        </div>
        {feedback.msg ? (
          <p className={`profile-modal-feedback ${feedback.type}`} role="status">
            {feedback.msg}
          </p>
        ) : null}
      </section>
    </AutoModel>
  );
};